import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, Divider, Typography } from '@mui/material';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PieChartOutlinedIcon from '@mui/icons-material/PieChartOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MonitorOutlinedIcon from '@mui/icons-material/MonitorOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useDispatch } from 'react-redux';
import { fetchClientDetails, } from '../../../redux/clientDataSlice';
import { useProjectOptions } from '../../../context/ProjectOptionsContext';
import MultiSelectInput from '../../../common_components/MultiSelectInput';
import { TextInput } from '../../../common_components/FormInputs';
import HandlerRow, { isEmptyIcpValue } from './HandlerRow';
// Fallback sample status options used before API responds
const SAMPLE_STATUS_FALLBACK = [
    { value: 'case_by_case', label: 'Case-by-Case Basis' },
    { value: 'no', label: 'No' },
    { value: 'regional_availability', label: 'Regional Availability' },
    { value: 'trials_rentals_available', label: 'Trials/Rentals Available' },
    { value: 'yes_not_on_samplify', label: 'Yes (Not on Samplify)' },
    { value: 'yes_on_samplify', label: 'Yes (on Samplify)' },
    { value: 'yes_with_client_approval', label: 'Yes w/ client approval' },
];
const ICON_SX = { fontSize: 16, color: '#667085' };
const ROW_ICON_SX = { fontSize: 14, color: '#98A2B3' };
const CardHeader = ({ icon, title }) => (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', color: '#667085' }, children: icon }), _jsx(Typography, { sx: { fontSize: '13px', fontWeight: 600, color: '#111827', flex: 1, lineHeight: 1.3 }, children: title })] }), _jsx(Divider, { sx: { mb: '4px', borderColor: '#F2F4F7' } })] }));
const ExpandedRow = ({ client, onAddNetwork, onIcpDraftChange, canManage }) => {
    const dispatch = useDispatch();
    const { sampleStatusOptions: contextSampleStatusOptions } = useProjectOptions();
    const sampleStatusOptions = contextSampleStatusOptions.length > 0 ? contextSampleStatusOptions : SAMPLE_STATUS_FALLBACK;
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const fetched = useRef(false);
    /* Parse stored sample_status (comma-separated or single) to string[] of option.value */
    const parseSampleStatus = useCallback((raw) => {
        if (raw == null || String(raw).trim() === '')
            return [];
        const parts = String(raw)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        return parts.map((labelOrValue) => {
            const lower = labelOrValue.toLowerCase();
            const match = sampleStatusOptions.find((opt) => opt.value.toLowerCase() === lower ||
                opt.label.toLowerCase() === lower);
            // Prefer internal option.value; fall back to original string so we never lose data
            return match ? match.value : labelOrValue;
        });
    }, [sampleStatusOptions]);
    /* ── local editable ICP state ── */
    const icp = client.icp;
    const [aliasValue, setAliasValue] = useState(icp?.alias ?? '');
    const [sampleStatusValues, setSampleStatusValues] = useState(() => parseSampleStatus(icp?.sample_status));
    const [brandDescValue, setBrandDesc] = useState(icp?.brand_description ?? '');
    /* helper to notify parent about current draft + dirty state (sample_status sent as comma-joined label string) */
    const notifyDraftChange = useCallback((nextAlias, nextSampleValues, nextBrand) => {
        if (!onIcpDraftChange)
            return;
        // Convert internal values back to labels for storage
        const toLabel = (val) => {
            const opt = sampleStatusOptions.find((o) => o.value === val);
            return opt?.label ?? val;
        };
        const nextSampleStr = nextSampleValues.map(toLabel).join(', ');
        const currentSampleStr = (icp?.sample_status ?? '').trim();
        const currentParsed = parseSampleStatus(icp?.sample_status);
        const currentJoined = currentParsed.map(toLabel).join(', ');
        const originalAlias = icp?.alias ?? '';
        const originalBrand = icp?.brand_description ?? '';
        const changedFields = {};
        if ((nextAlias ?? '') !== originalAlias)
            changedFields.alias = nextAlias;
        if (nextSampleStr !== currentJoined)
            changedFields.sample_status = nextSampleStr;
        if ((nextBrand ?? '') !== originalBrand)
            changedFields.brand_description = nextBrand;
        const dirty = Object.keys(changedFields).length > 0;
        onIcpDraftChange({
            clientId: client._id,
            alias: nextAlias,
            sample_status: nextSampleStr,
            brand_description: nextBrand,
            changedFields,
            dirty,
        });
    }, [onIcpDraftChange, client._id, icp?.alias, icp?.sample_status, icp?.brand_description, parseSampleStatus, sampleStatusOptions]);
    /* Sync from icp only when data has changed from outside (e.g. initial load or after save), so we don't overwrite user typing */
    const lastSyncedRef = useRef(null);
    useEffect(() => {
        const nextAlias = icp?.alias ?? '';
        const nextSampleStr = icp?.sample_status ?? '';
        const nextSampleValues = parseSampleStatus(icp?.sample_status);
        const nextBrand = icp?.brand_description ?? '';
        const prev = lastSyncedRef.current;
        const isExternalUpdate = prev === null ||
            prev.alias !== nextAlias ||
            prev.sample_status !== nextSampleStr ||
            prev.brand_description !== nextBrand;
        if (isExternalUpdate) {
            lastSyncedRef.current = { alias: nextAlias, sample_status: nextSampleStr, brand_description: nextBrand };
            setAliasValue(nextAlias);
            setSampleStatusValues(nextSampleValues);
            setBrandDesc(nextBrand);
            if (onIcpDraftChange) {
                onIcpDraftChange({
                    clientId: client._id,
                    alias: nextAlias,
                    sample_status: nextSampleValues.join(', '),
                    brand_description: nextBrand,
                    changedFields: {},
                    dirty: false,
                });
            }
        }
    }, [icp?.alias, icp?.sample_status, icp?.brand_description, client._id, onIcpDraftChange, parseSampleStatus]);
    useEffect(() => {
        if (fetched.current)
            return;
        fetched.current = true;
        setLoading(true);
        dispatch(fetchClientDetails({ clientId: client._id }))
            .unwrap()
            .then((data) => setDetail(data))
            .catch(() => setDetail(null))
            .finally(() => setLoading(false));
    }, [dispatch, client._id]);
    const handlers = detail?.accountHandlers ?? null;
    const toUsers = useCallback((field) => {
        if (!field)
            return [];
        const arr = Array.isArray(field) ? field : [field];
        return arr
            .filter(Boolean)
            .map((u) => typeof u === 'object'
            ? { name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || '' }
            : { name: String(u) })
            .filter((u) => u.name !== '');
    }, []);
    /* Affiliate network labels from icp.affiliate_network + icp.multiple_networks.
       Keep duplicates so UI count matches drawer rows exactly. */
    const networkNames = useMemo(() => {
        const names = [];
        const addName = (raw) => {
            const trimmed = raw.trim();
            if (!trimmed)
                return;
            names.push(trimmed);
        };
        if (icp?.affiliate_network)
            addName(icp.affiliate_network);
        (icp?.multiple_networks ?? []).forEach((m) => {
            addName(m?.affiliate_network || '');
        });
        return names;
    }, [icp?.affiliate_network, icp?.multiple_networks]);
    const website = icp?.website ?? client.website_url ?? null;
    const websiteHref = website
        ? website.startsWith('http') ? website : `https://${website}`
        : '#';
    if (loading) {
        return (_jsx(Box, { sx: { p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(CircularProgress, { size: 22 }) }));
    }
    return (_jsx(Box, { className: 'active-client-expanded-row', sx: {
            p: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            backgroundColor: '#F2F2F2',
        }, children: _jsxs(Box, { className: 'active-client-expanded-panels active-client-expanded-top', sx: {
                display: 'grid',
                gridTemplateColumns: '3fr 1fr',
                gap: '10px',
                minWidth: 0,
            }, children: [_jsxs(Box, { sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        minWidth: 0,
                    }, children: [_jsxs(Box, { sx: {
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                                gap: '10px',
                                minWidth: 0,
                            }, children: [_jsxs(Box, { className: 'active-client-expanded-card', children: [_jsx(CardHeader, { icon: _jsx(PeopleOutlinedIcon, { sx: ICON_SX }), title: "Team" }), _jsx(HandlerRow, { label: 'Lead AM', users: toUsers(handlers?.am_id), icon: _jsx(PersonOutlinedIcon, { sx: ROW_ICON_SX }) }), _jsx(HandlerRow, { label: 'Past AM', users: toUsers(handlers?.past_am_id), icon: _jsx(PersonOutlinedIcon, { sx: ROW_ICON_SX }) }), _jsx(HandlerRow, { label: 'AM', users: toUsers(handlers?.assoc_am_id), icon: _jsx(PersonOutlinedIcon, { sx: ROW_ICON_SX }) }), _jsx(HandlerRow, { label: 'Social Lead', users: toUsers(handlers?.paid_social_id), icon: _jsx(PersonOutlinedIcon, { sx: ROW_ICON_SX }) }), _jsx(HandlerRow, { label: 'Social Ass.', users: toUsers(handlers?.assoc_paid_social_id), icon: _jsx(PersonOutlinedIcon, { sx: ROW_ICON_SX }) })] }), _jsxs(Box, { className: 'active-client-expanded-card', children: [_jsx(CardHeader, { icon: _jsx(PieChartOutlinedIcon, { sx: ICON_SX }), title: "Lead Owners" }), _jsx(HandlerRow, { label: 'Search Lead', users: toUsers(handlers?.paid_search_id), icon: _jsx(SearchOutlinedIcon, { sx: ROW_ICON_SX }) }), _jsx(HandlerRow, { label: 'Search Ass.', users: toUsers(handlers?.assoc_paid_search_id), icon: _jsx(SearchOutlinedIcon, { sx: ROW_ICON_SX }) }), _jsx(HandlerRow, { label: 'Email Lead', users: toUsers(handlers?.email_lead_id), icon: _jsx(EmailOutlinedIcon, { sx: ROW_ICON_SX }) }), _jsx(HandlerRow, { label: 'Email Ass.', users: toUsers(handlers?.assoc_email_lead_id), icon: _jsx(EmailOutlinedIcon, { sx: ROW_ICON_SX }) }), _jsx(HandlerRow, { label: 'OS', users: toUsers(handlers?.assoc_affiliate_lead_id), icon: _jsx(MonitorOutlinedIcon, { sx: ROW_ICON_SX }) }), _jsx(HandlerRow, { label: 'Design Lead', users: toUsers(handlers?.design_lead_id), icon: _jsx(EditOutlinedIcon, { sx: ROW_ICON_SX }) })] }), _jsxs(Box, { className: 'active-client-expanded-card', sx: { display: 'flex', flexDirection: 'column' }, children: [_jsx(CardHeader, { icon: _jsx(StorageOutlinedIcon, { sx: ICON_SX }), title: "Company Size in Revenue" }), _jsx(HandlerRow, { variant: 'icp', label: 'Company Size in Revenue', value: icp?.company_size_in_revenue }), _jsx(HandlerRow, { variant: 'icp', label: 'Employee Count', value: icp?.employee_count }), _jsx(HandlerRow, { variant: 'icp', label: 'B2B/B2C', value: icp?.b2B_or_b2C }), _jsx(HandlerRow, { variant: 'icp', label: 'App/Services/Retail', value: icp?.app_services_retail })] }), _jsxs(Box, { className: 'active-client-expanded-card', sx: { display: 'flex', flexDirection: 'column' }, children: [_jsx(CardHeader, { icon: _jsx(PublicOutlinedIcon, { sx: ICON_SX }), title: "Primary Region" }), _jsx(HandlerRow, { variant: 'icp', label: 'Primary Region', value: icp?.primary_region ?? icp?.geo }), _jsx(HandlerRow, { variant: 'icp', label: 'Organic Traffic (Before)', value: icp?.organic_traffic_before_we_start_with_them }), !isEmptyIcpValue(icp?.three_percent_fee_increase) && (_jsx(HandlerRow, { variant: 'icp', label: '3% Fee Increase', value: icp?.three_percent_fee_increase }))] })] }), _jsxs(Box, { className: 'active-client-expanded-card', sx: {
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                gap: '10px',
                                minWidth: 0,
                                opacity: canManage ? 1 : 0.6,
                            }, children: [_jsxs(Box, { children: [_jsx(Box, { className: 'flex-container', sx: { mb: '4px' }, children: _jsx(Typography, { className: 'table-head-title', children: "Alias" }) }), _jsx(TextInput, { label: undefined, value: aliasValue, placeholder: '', size: 'md', fullWidth: true, disabled: !canManage, onChange: (e) => {
                                                if (!canManage)
                                                    return;
                                                const val = e.target.value;
                                                setAliasValue(val);
                                                notifyDraftChange(val, sampleStatusValues, brandDescValue);
                                            } })] }), _jsxs(Box, { children: [_jsx(Box, { className: 'flex-container', sx: { mb: '4px' }, children: _jsx(Typography, { className: 'table-head-title', children: "Sample Status" }) }), _jsx(MultiSelectInput, { label: '', name: `sample-status-${client._id}`, value: sampleStatusValues, options: sampleStatusOptions.map((o) => ({ value: o.value, label: o.label })), placeholder: 'Select options', maxDisplay: 2, disabled: !canManage, onChange: (e) => {
                                                if (!canManage)
                                                    return;
                                                const next = e.target.value;
                                                setSampleStatusValues(next);
                                                notifyDraftChange(aliasValue, next, brandDescValue);
                                            } })] })] })] }), _jsxs(Box, { className: 'active-client-expanded-card', sx: { display: 'flex', flexDirection: 'column', gap: '8px', opacity: canManage ? 1 : 0.6 }, children: [_jsxs(Box, { children: [_jsxs(Box, { className: 'flex-container', sx: { justifyContent: 'space-between', mb: '6px' }, children: [_jsx(Typography, { className: 'table-head-title', children: "Affiliate Network" }), canManage && (_jsx(Typography, { className: 'active-client-add-network-link', onClick: () => onAddNetwork(client), children: "+ Add Network" }))] }), _jsx(Box, { className: 'affiliate-network-readonly', sx: {
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                        minHeight: 40,
                                        alignItems: 'center',
                                        padding: '8px 14px',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        backgroundColor: !canManage ? '#F5F5F5' : 'background.paper',
                                    }, children: networkNames.length === 0 ? (_jsx(Typography, { variant: 'body2', color: 'text.secondary', children: "Affiliate Network" })) : (networkNames.map((name, idx) => {
                                        const colors = [
                                            { bg: '#F9F5FF', label: '#6941C6' },
                                            { bg: '#EFF8FF', label: '#175CD3' },
                                            { bg: '#EEF4FF', label: '#3538CD' },
                                        ];
                                        const c = colors[idx % colors.length];
                                        return (_jsx(Chip, { label: name, size: 'small', sx: {
                                                backgroundColor: c.bg,
                                                color: c.label,
                                                fontWeight: 500,
                                                fontSize: '14px',
                                                height: 24,
                                                borderRadius: '16px',
                                                '& .MuiChip-label': { color: c.label },
                                            } }, name));
                                    })) })] }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { sx: { mb: '4px' }, children: "Brand Description" }), _jsx(Box, { className: 'active-client-expanded-brand-box', sx: { backgroundColor: !canManage ? '#F5F5F5' : '#ffffff' }, children: _jsx("textarea", { className: 'active-client-expanded-value', value: brandDescValue, placeholder: '', onChange: (e) => {
                                            if (!canManage)
                                                return;
                                            const val = e.target.value;
                                            setBrandDesc(val);
                                            notifyDraftChange(aliasValue, sampleStatusValues, val);
                                        }, readOnly: !canManage, style: {
                                            width: '100%',
                                            height: 100,
                                            resize: 'none',
                                            border: 'none',
                                            background: 'transparent',
                                            outline: 'none',
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                            lineHeight: 1.5,
                                            color: 'inherit',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            padding: 0,
                                            cursor: canManage ? 'text' : 'default',
                                        } }) })] })] })] }) }));
};
export default ExpandedRow;
